import { formatErrorResponse } from "@utils/helperFunctions";
import type { AdminFormToast } from "./useAdminFormPersistence";

/** The kind of mutation that failed, used to phrase the message. */
export type SaveAction = "save" | "remove";

const ACTION_WORDS: Record<SaveAction, { infinitive: string; gerund: string }> =
  {
    save: { infinitive: "save", gerund: "saving" },
    remove: { infinitive: "remove", gerund: "removing" },
  };

/**
 * Maps an RTK Query mutation rejection (from `.unwrap()`) to a danger
 * `AdminFormToast`, so failures surface a meaningful message instead of a
 * silent `console.error`.
 *
 * RTK Query rejects with a `FetchBaseQueryError`: `{ status, data }`, where
 * `status` is the numeric HTTP code for server errors and `data` is the parsed
 * JSON:API body. (This is NOT the axios shape `err.response.status` that the
 * legacy `formatUnprocessibleResponse` expects.)
 *
 * - 403 -> the server's `errors[0].detail` if present, else a fixed message
 *   telling the user their role lacks permission.
 * - 422 -> field-level detail, reusing the established `formatErrorResponse`
 *   parsing so messages read like "Could not save approval. Name can't be blank".
 * - anything else (other 4xx/5xx, network/parse errors) -> a generic
 *   "an error occurred" message.
 *
 * @param noun    what was acted on, e.g. "approval" or "approver".
 * @param action  "save" (default) or "remove" — phrases the message for the
 *                kind of mutation that failed.
 */
export const saveErrorToast = (
  err: unknown,
  noun: string,
  action: SaveAction = "save"
): AdminFormToast => {
  const status = (err as { status?: unknown } | null)?.status;
  const errors = (err as { data?: { errors?: unknown } } | null)?.data?.errors;
  const firstDetail = Array.isArray(errors)
    ? (errors[0] as { detail?: string } | undefined)?.detail
    : undefined;
  const { infinitive, gerund } = ACTION_WORDS[action];

  if (status === 403) {
    return {
      variant: "danger",
      text:
        firstDetail ||
        `Your role does not have sufficient permissions to ${infinitive} this record.`,
    };
  }

  if (status === 422 && Array.isArray(errors)) {
    return { variant: "danger", text: format422(errors, noun, infinitive) };
  }

  return {
    variant: "danger",
    text: `An error occurred while ${gerund} the ${noun}. Please try again.`,
  };
};

const capitalizeAttribute = (attribute: string) =>
  attribute.charAt(0).toUpperCase() +
  attribute.slice(1).replaceAll("-", " ").trim();

const format422 = (
  errors: unknown[],
  noun: string,
  infinitive: string
): string => {
  const parsed = formatErrorResponse({ errors }) ?? [];
  const messages = parsed
    .filter(
      (e: any): e is { attribute: string; message: string } =>
        !!e && typeof e.message === "string"
    )
    .map((e) => {
      const attribute = capitalizeAttribute(e.attribute ?? "");
      return attribute ? `${attribute} ${e.message}` : e.message;
    });

  return messages.length
    ? `Could not ${infinitive} ${noun}. ${messages.join(", ")}`
    : `Could not ${infinitive} ${noun}. Please check your input and try again.`;
};
